import React, { useEffect, useState } from "react";
import { Loader2, Trash2, RefreshCw, CheckCircle2, XCircle, Plus, Save, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import api from "@/lib/api";

/**
 * Team-scoped Laaffic credentials admin page.
 *
 * One team = one active Laaffic API account. Adding a new credential
 * set for a team that already has one auto-disables the previous row
 * (audit trail preserved server-side). All secrets are masked in the
 * UI — rotating requires pasting the new key.
 */
export default function IntegrationsPage() {
  const [items, setItems] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [testing, setTesting] = useState({});

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/integrations/laaffic"),
        api.get("/teams"),
      ]);
      setItems(r1.data.items || []);
      setTeams(r2.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runTest = async (id) => {
    setTesting((t) => ({ ...t, [id]: true }));
    try {
      const { data } = await api.post(`/admin/integrations/laaffic/${id}/test`);
      // Refresh so the list shows last_ok_at / last_error updates.
      await load();
      alert(data.ok
        ? `✓ Connection OK. Balance: ${data.balance ?? "n/a"}`
        : `✗ ${data.detail}`);
    } catch (e) {
      alert(`Test failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setTesting((t) => ({ ...t, [id]: false }));
    }
  };

  const removeCred = async (id) => {
    if (!window.confirm("Delete this credential set? Prefer 'disable' for audit trail.")) return;
    try {
      await api.delete(`/admin/integrations/laaffic/${id}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="integrations-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-slate-900">Laaffic API accounts</h1>
          <div className="text-sm text-slate-600 mt-1 max-w-2xl">
            One Laaffic tenant per team. Secrets are stored encrypted (Fernet) and only their last 4 chars are shown.
            Add or rotate credentials here; the rest of the app resolves the right creds automatically by team_id.
          </div>
          <div className="mt-2 flex gap-2 text-xs">
            <Link to="/admin/laaffic-parity" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Parity report
            </Link>
            <Link to="/admin/laaffic-dispositions" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Disposition mapping
            </Link>
            <Link to="/admin/laaffic-backfill" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Backfill
            </Link>
            <Link to="/admin/laaffic-unmapped" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Unmapped queue
            </Link>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="int-reload">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Reload
          </Button>
          <Button size="sm" onClick={() => setShowForm(true)} data-testid="int-add">
            <Plus className="w-4 h-4 mr-1" /> Add credentials
          </Button>
        </div>
      </div>

      {err && (
        <div className="glass-card p-3 text-sm text-red-700 border-l-4 border-red-500" data-testid="int-err">{err}</div>
      )}

      {showForm && (
        <AddCredForm
          teams={teams}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}

      <div className="glass-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-slate-500 text-left">
              <th className="px-3 py-2">Team</th>
              <th className="px-3 py-2">Voice App ID</th>
              <th className="px-3 py-2">SMS App ID</th>
              <th className="px-3 py-2">API key</th>
              <th className="px-3 py-2">Base URL</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Last check</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
              </td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">
                No Laaffic credentials configured yet. Click <b>Add credentials</b> to create the first one.
              </td></tr>
            )}
            {items.map((row) => {
              const teamName = teams.find((t) => t.id === row.team_id)?.name || row.team_id.slice(0, 8);
              const ct = row.connection_test || {};
              return (
                <tr key={row.id}
                    className={`border-t border-slate-100 ${row.status === "disabled" ? "opacity-50" : ""}`}
                    data-testid={`int-row-${row.id}`}>
                  <td className="px-3 py-2 font-medium text-slate-900">{teamName}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{row.app_id}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-500">
                    {row.sms_app_id || "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-500">
                    {row.api_key_masked || "—"}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-500">{row.base_url}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      row.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"
                    }`}>{row.status}</span>
                  </td>
                  <td className="px-3 py-2 text-[11px]">
                    {ct.last_ok_at ? (
                      <span className="text-emerald-700 inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> {new Date(ct.last_ok_at).toLocaleString()}
                      </span>
                    ) : ct.last_error ? (
                      <span className="text-red-700 inline-flex items-center gap-1" title={ct.last_error}>
                        <XCircle className="w-3 h-3" /> {String(ct.last_error).slice(0, 40)}
                      </span>
                    ) : <span className="text-slate-400">never</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1.5">
                      <Button size="sm" variant="outline"
                              onClick={() => runTest(row.id)}
                              disabled={testing[row.id]}
                              data-testid={`int-test-${row.id}`}>
                        {testing[row.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : "Test"}
                      </Button>
                      <Button size="sm" variant="outline"
                              onClick={() => removeCred(row.id)}
                              data-testid={`int-del-${row.id}`}>
                        <Trash2 className="w-3 h-3 text-red-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Modal-style form to add a new Laaffic credential set. */
function AddCredForm({ teams, onClose, onSaved }) {
  const [form, setForm] = useState({
    team_id: "",
    app_id: "",
    sms_app_id: "",
    api_key: "",
    api_secret: "",
    base_url: "https://api.laaffic.com",
    webhook_secret: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      await api.post("/admin/integrations/laaffic", form);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const bind = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center p-4"
         onClick={onClose} data-testid="int-add-overlay">
      <form onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            className="bg-white rounded-lg shadow-xl max-w-lg w-full mt-16 p-5 space-y-3"
            data-testid="int-add-form">
        <div className="font-outfit font-semibold text-slate-900 text-lg">Add Laaffic credentials</div>

        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500">Team *</label>
          <select className="glass-input w-full h-9 mt-1" required
                  value={form.team_id} onChange={bind("team_id")}
                  data-testid="int-form-team">
            <option value="">— pick a team —</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500">Voice App ID *</label>
          <input className="glass-input w-full h-9 mt-1" required
                 value={form.app_id} onChange={bind("app_id")}
                 placeholder="e.g. voice_XXXX"
                 data-testid="int-form-appid" />
          <div className="text-[10px] text-slate-500 mt-0.5">
            From Laaffic console → SMS/Voice App → your <b>Voice</b> application row.
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500">SMS App ID (optional)</label>
          <input className="glass-input w-full h-9 mt-1"
                 value={form.sms_app_id} onChange={bind("sms_app_id")}
                 placeholder="e.g. sms_XXXX — only if you also send SMS via Laaffic"
                 data-testid="int-form-smsappid" />
          <div className="text-[10px] text-slate-500 mt-0.5">
            Same account, different app. Leave blank if you only use Laaffic for voice.
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500">API Key *</label>
          <input className="glass-input w-full h-9 mt-1 font-mono text-xs" required
                 value={form.api_key} onChange={bind("api_key")}
                 data-testid="int-form-apikey" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500">API Secret *</label>
          <input className="glass-input w-full h-9 mt-1 font-mono text-xs" required
                 type="password"
                 value={form.api_secret} onChange={bind("api_secret")}
                 data-testid="int-form-apisecret" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500">Base URL</label>
          <input className="glass-input w-full h-9 mt-1 font-mono text-xs"
                 value={form.base_url} onChange={bind("base_url")}
                 data-testid="int-form-baseurl" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500">Webhook secret (optional)</label>
          <input className="glass-input w-full h-9 mt-1 font-mono text-xs"
                 value={form.webhook_secret} onChange={bind("webhook_secret")}
                 data-testid="int-form-webhook" />
          <div className="text-[10px] text-slate-500 mt-0.5">Used to verify inbound webhook signatures. Leave blank for now — you can add it later.</div>
        </div>

        {err && <div className="text-sm text-red-600" data-testid="int-form-err">{err}</div>}

        <div className="flex gap-2 justify-end pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="int-form-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                    : <><Save className="w-4 h-4 mr-2" /> Save</>}
          </Button>
        </div>
      </form>
    </div>
  );
}
